export type TMasterCodeUsage = {
    mappingCount: number;
    /** Distinct owner names (business partners or deals), sorted. */
    usedBy: string[];
};

export interface TMasterCodeUsageNoticeProps {
    usage: TMasterCodeUsage | null;
    isLoading: boolean;
    hasError: boolean;
    /** Who owns the mappings, e.g. business partners for CoA, deals for propcodes. */
    ownerNoun: { one: string; many: string };
    /** What happens to those mappings once the code is deleted. */
    consequence: string;
}

const MAX_NAMES = 10;

/**
 * The in-use warning on a master CoA / propcode delete confirmation: who still
 * maps onto the code, and what deleting it does to them. Renders nothing for an
 * unused code, so the plain confirmation stays as it was.
 */
export function MasterCodeUsageNotice({
    usage,
    isLoading,
    hasError,
    ownerNoun,
    consequence,
}: TMasterCodeUsageNoticeProps) {
    if (isLoading) {
        return <p className="text-sm text-muted-foreground">Checking where this code is used…</p>;
    }
    if (hasError) {
        return (
            <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                Could not check whether this code is still mapped.
            </p>
        );
    }
    if (!usage || usage.mappingCount === 0) return null;

    const shown = usage.usedBy.slice(0, MAX_NAMES);
    const hidden = usage.usedBy.length - shown.length;
    const mappings = usage.mappingCount === 1 ? "mapping" : "mappings";
    const owners = usage.usedBy.length === 1 ? ownerNoun.one : ownerNoun.many;

    return (
        <div
            role="alert"
            data-testid="master-code-usage-notice"
            className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800"
        >
            <p className="font-medium">
                Still used by {usage.mappingCount} active {mappings} across {usage.usedBy.length}{" "}
                {owners}:
            </p>
            <ul className="mt-1 list-disc pl-5">
                {shown.map((name) => (
                    <li key={name}>{name}</li>
                ))}
                {hidden > 0 && <li>…and {hidden} more</li>}
            </ul>
            <p className="mt-2">{consequence}</p>
        </div>
    );
}
